import React from "react";
import { Box } from "@material-ui/core";
import {
  MultiSelect,
  CTextField,
  HFlex,
  ProgressButton,
  Text,
  ErrorBox,
  DialogSuccessNotice,
  useErrorContext,
} from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { PhoneNumberField, withOptionalPhone } from "shared/PhoneNumberInput";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import useInvitePartner, { ClinicPartnerPayload } from "../../queries/useInvitePartner";
import { delay } from "utils/misc";
import { FORM_NOTIFICATION_DELAY } from "theme";
import { useMountState } from "utils/hooks";
import { css } from "@emotion/css";
import { useUsProviderGroupClinicOptions } from "../utils/useUsProviderGroupClinicOptions";

interface Props {
  onClose: () => void;
}

export const InvitePartnerForm: React.VFC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const getMountState = useMountState();
  const { options: usProviderGroupClinicOptions, error, isError, isLoading } = useUsProviderGroupClinicOptions();
  const invitePartner = useInvitePartner();

  const form = useForm<ClinicPartnerPayload>({
    mode: "onChange",
  });

  const submit = form.handleSubmit(async fields => {
    try {
      await invitePartner.mutateAsync({
        ...withOptionalPhone(fields),
        us_provider_group_clinic_ids: fields.us_provider_group_clinic_ids.map(Number),
        // TODO: Remove when not needed by API
        // @ts-ignore
        // sources: ["TEST"],
      });
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      if (!getMountState()) return;
      onClose();
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (invitePartner.isSuccess)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${invitePartner?.data?.email}`}
        onClose={onClose}
      />
    );

  return (
    <Box mx={3}>
      <Text heading bold mb={1} id="invite-partner">
        {t("form.invite_partner")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField
            required
            name="email"
            autoFocus
            label={t("form.email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />
          <PhoneNumberField containerStyle={{ width: "100%", margin: "16px 0 8px" }} inputStyle={{ width: "100%" }} />
          <HFlex gap={2}>
            <CTextField
              fullWidth
              name="first_name"
              label={t("form.first_name")}
              margin="normal"
              size="small"
              required
            />
            <CTextField fullWidth name="last_name" label={t("form.last_name")} margin="normal" size="small" required />
          </HFlex>
          <div className={styles.spanRow}>
            <MultiSelect
              name="us_provider_group_clinic_ids"
              label={t("form.which_providers")}
              options={usProviderGroupClinicOptions}
              getOptionLabel={({ label }) => label ?? "NAME MISSING"}
              getOptionValue={({ value }) => value}
              loading={isLoading}
              margin="normal"
              multiple
              noOptionsText={isError && <ErrorBox error={error} />}
              size="small"
              disableCloseOnSelect
            />
          </div>
          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={invitePartner.isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};

const styles = {
  label: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  spanRow: css`
    grid-column: 1 / span 2;
  `,
};
